import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowClockwise, SignOut } from "@phosphor-icons/react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("vr_admin_token");

  const logout = useCallback(() => {
    localStorage.removeItem("vr_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const fetchLeads = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token, navigate, logout]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return (
    <main className="min-h-screen bg-cream px-6 py-12 font-sans lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-forest/70">VR Ahmedabad Realtors</p>
            <h1 className="mt-2 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">
              Enquiry Leads <span className="text-gold">({leads.length})</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="leads-refresh-btn"
              onClick={fetchLeads}
              className="flex items-center gap-2 rounded-sm border border-forest/20 bg-white px-5 py-2.5 text-sm font-semibold text-forest transition-colors duration-200 hover:border-gold"
            >
              <ArrowClockwise size={16} weight="bold" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-sm bg-forest px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-forest-hover"
            >
              <SignOut size={16} weight="bold" className="text-gold" /> Logout
            </button>
          </div>
        </div>

        <div data-testid="leads-table-wrapper" className="mt-8 overflow-x-auto rounded-lg border border-black/5 bg-white">
          {loading ? (
            <p className="p-10 text-center text-sm text-slate-400">Loading leads...</p>
          ) : leads.length === 0 ? (
            <p data-testid="leads-empty-state" className="p-10 text-center text-sm text-slate-400">
              No enquiries yet. New form submissions will appear here.
            </p>
          ) : (
            <table data-testid="leads-table" className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-black/5 bg-sand/60 text-xs uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Config</th>
                  <th className="px-5 py-4">Project</th>
                  <th className="px-5 py-4">Source</th>
                  <th className="px-5 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-black/5 last:border-0 hover:bg-cream/60">
                    <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                      {lead.created_at ? format(new Date(lead.created_at), "dd MMM, hh:mm a") : "—"}
                    </td>
                    <td className="px-5 py-4 font-semibold text-slate-900">{lead.name}</td>
                    <td className="px-5 py-4">
                      <a href={`tel:${lead.phone}`} className="text-forest hover:underline">
                        {lead.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{lead.email || "—"}</td>
                    <td className="px-5 py-4">
                      {lead.configuration ? (
                        <span className="rounded-full bg-forest/10 px-3 py-1 text-xs font-semibold text-forest">
                          {lead.configuration}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-5 py-4 font-medium text-slate-700">{lead.project || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{lead.utm_source || "direct"}</td>
                    <td className="max-w-[240px] truncate px-5 py-4 text-slate-600" title={lead.message || ""}>
                      {lead.message || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
}
